import { ChangeEvent, DragEvent, FC, useMemo, useRef, useState } from 'react';
import stylex from '@stylexjs/stylex';
import { Button, Input, TextField } from '@shared/ui/kit';
import { useToast } from '@shared/hooks/useToast';
import { useAuth } from '@features/auth';
import { parseRecipients } from './lib/parse-recipients';
import { readCsvFile } from './lib/read-csv-file';
import { useSendCredentials } from './api/useSendCredentials';
import { styles } from './styles';

/// Рассылка доступна только этой учётной записи — бэкенд проверяет то же самое
const ALLOWED_EMAIL = 'superadmin@sminex.com';

const DEFAULT_BODY = [
  '<p>Вам предоставлен доступ к <a href="https://events.sminex.tech/">https://events.sminex.tech/</a></p>',
  '<p>',
  '  Логин: {{login}}<br>',
  '  Пароль: {{password}}',
  '</p>',
  '<p>Запрещена передача данных третьим лицам.</p>',
].join('\n');

export const CredentialsMailingPage: FC = () => {
  const {
    data: { email },
  } = useAuth();
  const { showToast } = useToast();
  const { fetching, sendCredentials } = useSendCredentials();

  const [rawList, setRawList] = useState('');
  const [fileName, setFileName] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState('Sminex Event: вам предоставлен доступ');
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY);

  const { recipients, problems } = useMemo(
    () => (rawList.trim() ? parseRecipients(rawList) : { recipients: [], problems: [] }),
    [rawList]
  );
  const isAllowed = email === ALLOWED_EMAIL;

  if (!isAllowed) {
    return (
      <div {...stylex.props(styles.page)}>
        <p {...stylex.props(styles.denied)}>Раздел доступен только администратору рассылки.</p>
      </div>
    );
  }

  const loadFile = async (file: File | undefined) => {
    if (!file) return;

    try {
      setRawList(await readCsvFile(file));
      setFileName(file.name);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Не удалось прочитать файл', 'error');
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    loadFile(event.dataTransfer.files?.[0]);
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    loadFile(event.target.files?.[0]);
    // сбрасываем, иначе повторный выбор того же файла не вызовет onChange
    event.target.value = '';
  };

  const send = async (testOnly: boolean) => {
    if (recipients.length === 0) {
      showToast('Список получателей пуст или не разобран', 'error');
      return;
    }
    if (!subject.trim() || !bodyHtml.trim()) {
      showToast('Заполните тему и текст письма', 'error');
      return;
    }
    if (!testOnly) {
      const confirmed = window.confirm(
        `Отправить письма с паролями ${recipients.length} получателям? Действие необратимо.`
      );
      if (!confirmed) return;
    }

    const result = await sendCredentials({
      input: { recipients, subject, bodyHtml, testOnly },
    });

    if (result.error) {
      showToast(result.error.message.replace('[GraphQL] ', ''), 'error');
      return;
    }

    const data = result.data?.sendCredentials;
    if (!data) return;

    if (data.failed > 0) {
      showToast(`Отправлено ${data.sent}, с ошибкой ${data.failed}`, 'error');
      data.errors.forEach((item) => showToast(`${item.email}: ${item.error}`, 'error'));
      return;
    }

    showToast(`Отправлено писем: ${data.sent}`, 'success');
  };

  return (
    <div {...stylex.props(styles.page)}>
      <h1 {...stylex.props(styles.title)}>Рассылка доступов</h1>
      <p {...stylex.props(styles.hint)}>
        Пароли не сохраняются: они используются только для письма и нигде не остаются — ни в базе, ни в журнале
        рассылок.
      </p>

      <section {...stylex.props(styles.section)}>
        <p {...stylex.props(styles.label)}>Список получателей</p>
        <p {...stylex.props(styles.hint)}>
          Перетащите CSV-файл или вставьте текст ниже. Первая строка — заголовок <code>email;login;password</code>.
          Разделитель «;», «,» или табуляция определяется автоматически.
        </p>

        <div
          {...stylex.props(styles.dropZone, isDragOver && styles.dropZoneActive)}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {fileName ? `Файл: ${fileName}` : 'Перетащите CSV-файл сюда или нажмите, чтобы выбрать'}
          <input ref={fileInputRef} type="file" accept=".csv,text/csv,text/plain" onChange={handleFileInput} hidden />
        </div>

        <TextField
          labelMsg=""
          placeholder={'email;login;password\nivanov@sminex.com;ivanov@sminex.com;пароль'}
          value={rawList}
          changeFunction={(e) => {
            setRawList(e.target.value);
            setFileName('');
          }}
          settings={{ width: '100%', height: '180px', resize: 'vertical' }}
        />
      </section>

      <section {...stylex.props(styles.section)}>
        <p {...stylex.props(styles.label)}>Тема письма</p>
        <Input size="medium" value={subject} changeFunction={setSubject} settings={{ width: '100%' }} />
      </section>

      <section {...stylex.props(styles.section)}>
        <p {...stylex.props(styles.label)}>Текст письма</p>
        <p {...stylex.props(styles.hint)}>
          Плейсхолдеры: <code>{'{{login}}'}</code>, <code>{'{{password}}'}</code>, <code>{'{{email}}'}</code>
        </p>
        <TextField
          labelMsg=""
          placeholder="HTML письма"
          value={bodyHtml}
          changeFunction={(e) => setBodyHtml(e.target.value)}
          settings={{ width: '100%', height: '160px', resize: 'vertical' }}
        />
      </section>

      <section {...stylex.props(styles.section)}>
        <p {...stylex.props(styles.summary)}>
          Распознано получателей: <b>{recipients.length}</b>
          {problems.length > 0 && <span {...stylex.props(styles.problemsCount)}> · отбраковано {problems.length}</span>}
        </p>
        {problems.length > 0 && (
          <ul {...stylex.props(styles.problems)}>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
      </section>

      <div {...stylex.props(styles.actions)}>
        <Button size="medium" variant="secondary" isDisabled={fetching} onClick={() => send(true)}>
          <p>Тест — только мне</p>
        </Button>
        <Button size="medium" variant="accent" isDisabled={fetching} onClick={() => send(false)}>
          <p>Отправить всем ({recipients.length})</p>
        </Button>
      </div>
    </div>
  );
};
